import type { FC } from 'react';
import { useState } from 'react';
import { NavLink, useNavigate } from 'react-router';

import { addNotification } from 'notifications';
import { Button, TextField } from 'ui';
import { getApiErrorMessage, getApiSuccessMessage } from 'utils/getApiErrorMessage';

import { forgotPasswordSchema } from './schema';
import { useResetRequestMutation } from './services';
import classes from '../Registration/register.module.scss';

import { Form, Formik } from 'formik';

export const ForgotPassword: FC = () => {
  const navigate = useNavigate();
  const resetRequest = useResetRequestMutation();
  // «Аккаунт не найден» - у поля, с переходом в регистрацию с тем же адресом или номером.
  const [notFound, setNotFound] = useState<{ message: string; login: string } | null>(null);

  return (
    <section className={classes.wrapper}>
      <main className={classes.container}>
        <Formik
          initialValues={{ email: '' }}
          validationSchema={forgotPasswordSchema}
          onSubmit={async (values) => {
            setNotFound(null);
            try {
              const data = await resetRequest.mutateAsync(values.email);
              if (data.status === 'account_not_found') {
                setNotFound({ message: getApiSuccessMessage(data, 'Аккаунт не найден.'), login: values.email.trim() });
                return;
              }
              addNotification(
                getApiSuccessMessage(data, 'Проверьте почту.'),
                data.email_sent ? 'success' : 'info',
              );
              if (data.status === 'verification_resent') {
                localStorage.setItem('registration_email', values.email);
                navigate('/registration/confirm');
                return;
              }
              if (data.status === 'otp_sent' && data.challenge_id) {
                const params = new URLSearchParams({
                  purpose: 'password_reset',
                  challenge_id: data.challenge_id,
                  channel: data.channel || '',
                  phone: values.email,
                });
                navigate(`/otp?${params.toString()}`);
                return;
              }
              if (data.email_sent || data.status === 'password_reset_sent') {
                navigate('/login');
              }
            } catch (error) {
              addNotification(
                getApiErrorMessage(error, 'Не удалось отправить запрос'),
                'error',
              );
            }
          }}
          validateOnChange={true}
          validateOnBlur={true}
        >
          {({ values, errors, touched, handleBlur, setFieldValue }) => (
            <Form className={classes.form}>
              {/* Figma 21:6565: заголовок слева, серый подзаголовок, поле с подписью. */}
              <div className={classes.heading}>
                <h1 className={classes.title}>Забыли пароль?</h1>
                <p className={classes.subtitle}>
                  Введите email или телефон. Мы отправим ссылку на почту или код на номер.
                </p>
              </div>

              <div>
                <TextField
                  className='fig-auth-field'
                  shape='brick'
                  label='Почта или телефон'
                  placeholder='Email или телефон'
                  size='lg'
                  name='email'
                  value={values.email}
                  onChange={(val) => {
                    setNotFound(null);
                    setFieldValue('email', val);
                  }}
                  onBlur={handleBlur}
                  isInvalid={notFound?.message || (touched.email && errors.email)}
                  disabled={resetRequest.isPending}
                />
                {notFound && notFound.login ? (
                  <NavLink
                    to={
                      notFound.login.includes('@')
                        ? `/registration?email=${encodeURIComponent(notFound.login)}`
                        : `/registration?phone=${encodeURIComponent(notFound.login)}`
                    }
                    className={classes.link}
                    style={{ display: 'inline-block', marginTop: 8 }}
                  >
                    Зарегистрироваться
                  </NavLink>
                ) : null}
              </div>

              <Button
                type='submit'
                variant='filled'
                shape='brick'
                widthFullSize
                loading={resetRequest.isPending}
                // Неактивна, пока нет полного номера (как на сервере: 10 цифр или 11 с 7/8)
                // или адреса вида name@example.com. Считается по текущему значению поля.
                disabled={
                  resetRequest.isPending ||
                  !(values.email.includes('@')
                    ? /^\S+@\S+\.\S+$/.test(values.email.trim())
                    : /^(\d{10}|[78]\d{10})$/.test(values.email.replace(/\D/g, '')))
                }
                className={classes.submitButton}
              >
                Отправить
              </Button>

              <div className={classes.loginPrompt}>
                <NavLink to='/login' className={classes.link}>
                  Вернуться ко входу
                </NavLink>
              </div>
            </Form>
          )}
        </Formik>
      </main>
    </section>
  );
};
